<template>
  <div class="file-upload-question">
    <div class="upload-area" @dragover.prevent="isDragging = true" @dragleave.prevent="isDragging = false" @drop.prevent="handleFileDrop" :class="{ 'dragging': isDragging }">
      <div v-if="!uploadedFile" class="upload-content">
        <i class="bi bi-cloud-upload" style="font-size: var(--font-size-3xl); color: #007bff;"></i>
        <p class="mt-3 mb-2">
          <strong>Drag and drop your file here, or</strong>
        </p>
        <label class="btn btn-primary btn-sm">
          Browse Files
          <input type="file" @change="handleFileSelect" :accept="acceptedFileTypes" hidden>
        </label>
        <div class="mt-3 text-muted small">
          <p v-if="maxFileSize">Maximum file size: {{ maxFileSize }}MB</p>
          <p v-if="allowedMimeTypes.length" class="mb-0">
            Allowed types: {{ formatMimeTypes() }}
          </p>
        </div>
      </div>

      <div v-else class="upload-complete">
        <i class="bi bi-check-circle text-success" style="font-size: var(--font-size-3xl);"></i>
        <p class="mt-3 mb-2"><strong>{{ uploadedFile.name }}</strong></p>
        <small class="text-muted d-block mb-3">{{ formatFileSize(uploadedFile.size) }}</small>
        <button type="button" class="btn btn-sm btn-outline-danger" @click="clearUpload">
          <i class="bi bi-trash"></i> Remove File
        </button>
      </div>

      <div v-if="uploadError" class="alert alert-danger mt-3 mb-0">
        {{ uploadError }}
      </div>

      <div v-if="isUploading" class="mt-3">
        <div class="progress" style="height: 20px;">
          <div class="progress-bar" :style="{ width: uploadProgress + '%' }">{{ uploadProgress }}%</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FileUploadQuestion',
  props: {
    value: {
      type: String,
      default: ''
    },
    question: {
      type: Object,
      required: true
    },
    surveyId: {
      type: Number,
      required: true
    },
    userId: {
      type: Number
    }
  },
  data() {
    return {
      isDragging: false,
      uploadedFile: null,
      isUploading: false,
      uploadProgress: 0,
      uploadError: '',
      maxFileSize: this.question.maxFileSize || 10,
      allowedMimeTypes: this.question.allowedMimeTypes || []
    }
  },
  computed: {
    acceptedFileTypes() {
      if (this.allowedMimeTypes.length === 0) {
        return '*'
      }
      return this.allowedMimeTypes.join(',')
    }
  },
  watch: {
    value(newVal) {
      if (!newVal && this.uploadedFile) {
        this.clearUpload()
      }
    }
  },
  methods: {
    handleFileSelect(event) {
      const file = event.target.files[0]
      if (file) {
        this.processFile(file)
      }
    },
    handleFileDrop(event) {
      this.isDragging = false
      const file = event.dataTransfer.files[0]
      if (file) {
        this.processFile(file)
      }
    },
    processFile(file) {
      this.uploadError = ''

      if (this.validateFile(file)) {
        this.uploadFile(file)
      }
    },
    validateFile(file) {
      const maxBytes = this.maxFileSize * 1024 * 1024

      if (file.size > maxBytes) {
        this.uploadError = `File size exceeds maximum of ${this.maxFileSize}MB`
        return false
      }

      if (this.allowedMimeTypes.length > 0 && !this.allowedMimeTypes.includes(file.type)) {
        this.uploadError = `File type ${file.type} is not allowed`
        return false
      }

      return true
    },
    async uploadFile(file) {
      this.isUploading = true
      this.uploadProgress = 0

      try {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('surveyId', this.surveyId)
        formData.append('questionId', this.question.id)

        const config = {
          onUploadProgress: (progressEvent) => {
            this.uploadProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          }
        }

        const response = await this.$http.post('/api/v1/admin/surveys/upload-file', formData, config)

        if (response.data.success) {
          this.uploadedFile = {
            name: file.name,
            size: file.size,
            path: response.data.data.path,
            timestamp: response.data.data.timestamp
          }
          this.$emit('input', response.data.data.path)
        }
      } catch (error) {
        this.uploadError = error.response?.data?.message || 'Error uploading file'
        console.error('File upload error:', error)
      } finally {
        this.isUploading = false
      }
    },
    clearUpload() {
      this.uploadedFile = null
      this.uploadError = ''
      this.uploadProgress = 0
      this.$emit('input', '')
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
    },
    formatMimeTypes() {
      const typeMap = {
        'application/pdf': 'PDF',
        'application/msword': 'DOC',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
        'application/vnd.ms-excel': 'XLS',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
        'application/vnd.ms-powerpoint': 'PPT',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PPTX',
        'image/jpeg': 'JPG',
        'image/png': 'PNG',
        'image/gif': 'GIF',
        'image/svg+xml': 'SVG'
      }

      return this.allowedMimeTypes
        .map(type => typeMap[type] || type)
        .join(', ')
    }
  }
}
</script>

<style scoped>
.file-upload-question {
  width: 100%;
}

.upload-area {
  border: 2px dashed #ddd;
  border-radius: 8px;
  padding: 40px 20px;
  text-align: center;
  transition: all 0.3s ease;
  cursor: pointer;
}

.upload-area.dragging {
  border-color: #007bff;
  background-color: #f0f7ff;
}

.upload-content,
.upload-complete {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.upload-area .btn {
  margin-top: 10px;
}

@media (max-width: 480px) {
  .upload-area {
    padding: 30px 15px;
  }

  .upload-area i {
    font-size: var(--font-size-2xl) !important;
  }

  .upload-area p {
    font-size: var(--font-size-sm);
  }
}
</style>
